<template>
  <div class="signin-container">
    <div class="signin-card">
      <div class="signin-card-inner">
        <div class="signin-logo">
          <img :src="logoSrc">
          <div class="voice-analytics-title">
            {{ $t('vaApp.title') }}
          </div>
        </div>
        <div class="signin-message">
          {{ $t('auth.resetPageMessage') }}
        </div>
        <div class="signin-form">
          <div class="mb-600">
            <BInput
              v-model="email"
              :placeholder="$t('auth.email')"
              color="focus"
            />
          </div>
          <BBtn
            class="mb-400"
            :loading="wait.is('USER_RESET_PASSWORD_WAIT')"
            :disabled="!isValidEmail"
            type="primary"
            @click.prevent="resetPassword"
          >
            <span>{{ $t('auth.submit') }}</span>
          </BBtn>
          <BLayout
            align-center
            justify-center
          >
            <RouterLink :to="{ name: 'VaAppSignin' }">
              {{ $t('auth.returnSignin') }}
            </RouterLink>
          </BLayout>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import logoSrc from '@/assets/brand_logo.svg';
import { useConfirmWithOptions } from '@/composable/message-dialog';
import { useWait } from '@/composable/vue-wait';
import AuthService from '@/services/auth_service';

const router = useRouter();
const { wait, doActionWithWait } = useWait();
const i18n = useI18n();

const { confirmWithOptions } = useConfirmWithOptions();

const email = ref('');
const isValidEmail = computed(() => {
  return email.value.length > 0;
});

const authService = new AuthService();

const resetPassword = async () => {
  await doActionWithWait('USER_RESET_PASSWORD_WAIT', async () => {
    let bitterAlertTitle = i18n.t('auth.resetPassword');
    let bitterAlertText = i18n.t('auth.resetPasswordMessage');
    try {
      await authService.resetPassword(email.value);
      router.push({ name: 'VaAppSignin' });
    } catch (error) {
      bitterAlertTitle = i18n.t('auth.error.resetPasswordError');
      switch (error.code) {
        case 'auth/invalid-email':
        case 'auth/user-not-found':
          bitterAlertText = i18n.t('auth.errorMessage.wrongEmail');
          break;
        case 'auth/user-disabled':
          bitterAlertText = i18n.t('auth.errorMessage.userDisabled');
          break;
        case 'auth/too-many-requests':
          bitterAlertText = i18n.t('auth.errorMessage.tooManyRequests');
          break;
        default:
          bitterAlertText = error.message;
      }
    } finally {
      await confirmWithOptions({
        title: bitterAlertTitle,
        message: bitterAlertText,
        closeOnClickOutside: true,
        optionalButtonOptions: {
          confirm: {
            text: i18n.t('general.ok'),
          },
        },
      });
    }
  });
};
</script>

<style lang="scss" scoped>
.signin-card {
  background:  #EAF9F8;
  width: 480px;
  height: 100%;
  margin: 0 auto;
  box-shadow: $boxshadow-base;
  display: flex;
  padding-top: 60px;
  justify-content: center;
}

.signin-card-inner {
  display: flex;
  flex-direction: column;
  width: 280px;
}

.signin-logo {
  margin: $basespace-800 auto 60px auto;

  img {
    width: 100%;
    height: 60px;
  }

  .voice-analytics-title {
    padding: 8px 24px;
    border-radius: 32px;
    background: $basecolor-primary;
    color: $textcolor-white;
    font-size: 28px;
    font-weight: 500;
    margin-inline: auto;
    margin-top: 12px;
    width: fit-content;
  }
}


.signin-message {
  text-align: center;
  font-weight: bold;
  font-size: $fontsize-300;
  margin-bottom: $basespace-400;
}

.signin-container {
  background:
    linear-gradient(0deg, rgba(18, 76, 73, 0.30) 0%, rgba(18, 76, 73, 0.30) 100%),
    url('@/assets/signin-background_2.png');
  background-repeat: no-repeat;
  background-attachment: fixed;
  background-size: cover;

  height: 100vh;
}

.signin-form {
  text-align: center;
}
</style>
